# Advanced Patterns

Multi-field decisions and custom slots for complex widget architectures.

---

## Multi-Field Decisions

For multivariable UIs where different slots need independent control, logic statements can return multiple decision fields, such that different fields inform different slots.

### The Problem

Sometimes a single `nextTool` isn't enough. Consider an onboarding flow where after user creation:

- Primary CTA -> "Start Import" (recommended path)
- Secondary CTA -> "Manual Setup" (alternative path)
- Widget content -> "Path Choice" screen

These are independent decisions that shouldn't all key off the same field.

### The Solution

Return multiple fields from your logic statement. Each binding declares which decision field it matches - Taias automatically infers the mapping:

```ts
// flows.ts
flow.step(
  { toolName: { is: "createUser" } },
  {
    nextTool: "startImport", // For LLM advice
    primaryAction: "startImport", // For primary CTA selection
    secondaryAction: "startManualSetup", // For secondary CTA selection
    contentArea: "path-choice", // For widget variant selection
  },
);

// affordances.ts - each binding declares its key
const onboardingAffordances = defineAffordances((r) => {
  r.primaryCta("cta.start-import", {
    key: "primaryAction",
    value: "startImport",
  });
  r.secondaryCta("cta.start-manual", {
    key: "secondaryAction",
    value: "startManualSetup",
  });
  r.widgetVariant("content.path-choice", {
    key: "contentArea",
    value: "path-choice",
  });
});

// instance.ts
const taias = createTaias({
  flow: onboardingFlow,
  affordances: mergeAffordances([onboardingAffordances]),
});
```

### How It Works

1. Your logic statement produces a decision object with one or more fields (e.g., `{ primaryAction: "startImport", secondaryAction: "startManualSetup", contentArea: "path-choice" }`)
2. Each slot's decision field is inferred from its handle bindings
3. Selection matches `bindsTo.value` against `decision[bindsTo.key]`

### Key Constraint

All handles for the same slot must bind to the same decision field. If you register conflicting bindings:

```ts
// This will throw an error!
r.primaryCta("cta.a", { key: "fieldA", value: "x" });
r.primaryCta("cta.b", { key: "fieldB", value: "y" }); // Different key!
```

Taias throws a helpful error:

```
[Taias] Slot "primaryCta" has handles bound to different keys: "fieldA" and "fieldB".
All handles for a slot must use the same decision field.
```

### When to Use

- **Single next action:** Use `{ toolName: "x" }` shorthand in bindings - all slots match against `nextTool`
- **Independent slot control:** Use `{ key, value }` format to bind each slot to its own decision field

---

## Custom Slots

For complex widgets that need more than the three default slots, you can define your own slot types with full TypeScript type safety.

### Defining Custom Slots

Use a generic type parameter to specify your slot names:

```ts
// Define your custom slot type
type OnboardingSlots =
  | "primaryCta"
  | "secondaryCta"
  | "contentArea"
  | "headerStyle";

// Use it with defineAffordances
const affordances = defineAffordances<OnboardingSlots>((r) => {
  r.primaryCta("cta.import", { key: "primaryAction", value: "startImport" });
  r.contentArea("content.form", { key: "contentArea", value: "email-form" });
  r.headerStyle("header.progress", { key: "headerStyle", value: "step-1" });
  // r.invalidSlot("...", {...}); // Type error! Only slots in OnboardingSlots are allowed
});
```

### Full Custom Slots Example

```ts
// Define slots for a dashboard widget
type DashboardSlots = "headerWidget" | "mainPanel" | "sidebar";

const flow = defineFlow("dashboard", (f) => {
  f.step(
    { toolName: { is: "load_dashboard" } },
    {
      nextTool: "refresh",
      headerWidget: "stats",
      mainPanel: "chart",
      sidebar: "filters",
    },
  );
});

// Each binding declares its key - decision fields are inferred automatically
const affordances = defineAffordances<DashboardSlots>((r) => {
  r.headerWidget("header.stats", { key: "headerWidget", value: "stats" });
  r.mainPanel("main.chart", { key: "mainPanel", value: "chart" });
  r.sidebar("sidebar.filters", { key: "sidebar", value: "filters" });
});

const taias = createTaias<DashboardSlots>({
  flow,
  affordances: mergeAffordances([affordances]),
});

const result = await taias.resolve({ toolName: "load_dashboard" });
// result.selections.headerWidget?.handleId === "header.stats"
// result.selections.mainPanel?.handleId === "main.chart"
// result.selections.sidebar?.handleId === "sidebar.filters"
```

### Why Custom Slots?

Different widget architectures have different UI control points:

- **Simple widgets**: 1-2 CTAs (use default slots)
- **Complex widgets**: Multiple content areas, headers, footers
- **Dashboard widgets**: Multiple independent panels

Custom slots let you model your exact UI structure with full type safety - autocomplete, typo detection, and compile-time errors for invalid slot names.

### Backwards Compatibility

If you don't specify a type parameter, Taias uses the default slots (`primaryCta`, `secondaryCta`, `widgetVariant`). Existing code works unchanged:

```ts
// Still works exactly as before
const affordances = defineAffordances((r) => {
  r.primaryCta("cta.recommend", { toolName: "get_recommendations" });
});
```
